import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { CircleNotch } from "@phosphor-icons/react";
import { quizReportApi } from "./quizReportApi";
import CoverageTable from "./CoverageTable";
import CohortPanel from "./CohortPanel";

type Tab = "cohort" | "coverage";

/** Eğitmen ekranı: modül sonu pratiğin sınıf durumu + soru bankası kapsamı. */
export default function QuizReportMain() {
  const [tab, setTab] = useState<Tab>("cohort");
  const [cohortId, setCohortId] = useState<number | null>(null);

  const { data: cohorts } = useQuery({
    queryKey: ["qbCohorts"],
    queryFn: quizReportApi.cohorts,
    staleTime: 5 * 60 * 1000,
  });

  const { data: coverage, isLoading: coverageLoading } = useQuery({
    queryKey: ["qbCoverage"],
    queryFn: quizReportApi.coverage,
    enabled: tab === "coverage",
    staleTime: 5 * 60 * 1000,
  });

  const { data: report, isLoading: reportLoading } = useQuery({
    queryKey: ["qbCohortReport", cohortId],
    queryFn: () => quizReportApi.cohort(cohortId!),
    enabled: tab === "cohort" && cohortId !== null,
  });

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 flex flex-col gap-6">
      <div>
        <h1 className="text-lg font-semibold text-white/90">Modül Sonu Pratik — Rapor</h1>
        <p className="text-xs text-white/50 mt-1">
          Sınıfın hangi konularda zorlandığını ve soru bankasındaki boşlukları gösterir.
        </p>
      </div>

      <div className="flex gap-2">
        {(
          [
            ["cohort", "Sınıf"],
            ["coverage", "Soru Bankası"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={clsx(
              "px-4 py-2 rounded-xl text-sm transition",
              tab === key ? "bg-myGreen/15 text-myGreen" : "bg-myGb/30 text-white/60 hover:text-white",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "cohort" && (
        <>
          <select
            value={cohortId ?? ""}
            onChange={(e) => setCohortId(e.target.value ? Number(e.target.value) : null)}
            className="bg-myGb/40 border border-white/10 rounded-xl px-3 py-2 text-sm max-w-sm"
          >
            <option value="">Sınıf seç…</option>
            {cohorts?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} (#{c.id})
              </option>
            ))}
          </select>

          {cohortId === null ? (
            <p className="text-white/40 text-sm">Rapor için bir sınıf seç.</p>
          ) : reportLoading ? (
            <Spinner />
          ) : report ? (
            <CohortPanel report={report} />
          ) : null}
        </>
      )}

      {tab === "coverage" &&
        (coverageLoading ? <Spinner /> : coverage ? <CoverageTable rows={coverage} /> : null)}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-12 text-white/50">
      <CircleNotch size={24} className="animate-spin" />
    </div>
  );
}
